import React, { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import { Box, Button, ButtonGroup } from "@mui/material";

import ClaimsDashboard from "./subpages/ClaimsDashboard";
import ObservabilityDashboard from "./subpages/ObservabilityDashboard";
import Row from "../components/ui/core/Row";
import ObservabilityFilters from "../components/dashboard/components/observability/ObservabilityFilters";
import { useIsBillingMember } from "../contexts/permissions/useIsBillingMember";

type DashboardView = "claims" | "observability";

const VIEWS: { id: DashboardView; label: string }[] = [
  { id: "claims", label: "Claims" },
  { id: "observability", label: "Financial Impact" },
];

const parseTabFromSearchParams = (params: URLSearchParams): DashboardView => {
  const tab = params.get("tab");

  return tab === "observability" ? "observability" : "claims";
};

const Dashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // The Usage (observability) tab is restricted to the `billing` group ONLY
  // (strict — no admin bypass; see useIsBillingMember). Non-billing users get
  // just the Claims view: the tab selector is hidden entirely since there is
  // nothing to switch to, and a direct ?tab=observability URL falls back to Claims.
  const hasBillingPermission = useIsBillingMember();

  const view = useMemo(() => {
    const parsed = parseTabFromSearchParams(searchParams);
    if (parsed === "observability" && !hasBillingPermission) {
      return "claims";
    }
    return parsed;
  }, [searchParams, hasBillingPermission]);

  const setView = useCallback(
    (next: DashboardView) => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set("tab", next);
          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "secondary.main",
      }}
    >
      {/* The tab selector is shown only to billing-group users. Non-billing
          users have just the Claims view, so there is nothing to switch between
          and the toggle row is omitted.
          Horizontal padding matches the dashboard content below (padding: 4),
          so the toggle's left edge and the date selector's right edge line up
          with the cards. */}
      {hasBillingPermission ? (
        <Row
          sx={{ height: "unset", gap: 1, alignItems: "center", px: 4, py: "10px" }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <ButtonGroup size="small">
              {VIEWS.map((item) => (
                <Button
                  key={item.id}
                  size="small"
                  variant={view === item.id ? "contained" : "outlined"}
                  onClick={() => setView(item.id)}
                  sx={{
                    textTransform: "none",
                    minWidth: "84px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </ButtonGroup>
          </Box>

          {view === "observability" ? (
            <>
              <Box sx={{ flex: 1 }} />
              <Box sx={{ flexShrink: 0 }}>
                <ObservabilityFilters />
              </Box>
            </>
          ) : null}
        </Row>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {view === "claims" ? <ClaimsDashboard /> : <ObservabilityDashboard />}
      </Box>
    </Box>
  );
};

export default Dashboard;
